import { useEffect, useMemo, useState } from "react";

import { ReactComponent as AppStoreBadge } from "static/download-on-the-app-store-badge.svg";
import { ReactComponent as FarOutLogo } from "static/logo_farout_white.svg";
import { ReactComponent as PlayStoreBadge } from "static/download-on-the-play-store-badge.svg";
import { getPlatform } from "utils/systemUtils";

import { GO_LANDING_COPY } from "./GoLandingPage.copy";
import {
  APP_STORE_URL,
  buildOpenAppUrl,
  buildPlayStoreUrl,
  getLinkContext,
  getStoreRedirectUrl,
  isAutoStoreRedirectPath,
} from "./GoLandingPage.routing";
import { S } from "./GoLandingPage.styles";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

const CloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M6 6l12 12M18 6L6 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

export const GoLandingPage = () => {
  const parameters = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );
  const context = useMemo(() => getLinkContext(parameters), [parameters]);
  const openAppUrl = useMemo(
    () => buildOpenAppUrl(window.location.pathname, parameters),
    [parameters]
  );
  const playStoreUrl = useMemo(
    () => buildPlayStoreUrl(parameters),
    [parameters]
  );
  const platform = useMemo(() => getPlatform(), []);
  const [installPrompt, setInstallPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    // Only the /apps link auto-redirects; every other landing path is shown
    // as a regular page.
    if (!isAutoStoreRedirectPath(window.location.pathname)) return;

    // Mobile visitors reached the browser instead of the app, so the app is
    // not installed — send them straight to their store. The landing stays
    // rendered as a fallback if the navigation is blocked or the user
    // returns via back navigation.
    const storeUrl = getStoreRedirectUrl(platform, parameters);
    if (storeUrl) window.location.replace(storeUrl);
  }, [platform, parameters]);

  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]'
    );
    const previousDescription = description?.content;

    document.title = "Open in FarOut";
    if (description) {
      description.content =
        "Open this link in FarOut or download the app for trusted trail maps, waypoints, and community updates.";
    }

    return () => {
      document.title = previousTitle;
      if (description && previousDescription) {
        description.content = previousDescription;
      }
    };
  }, []);

  useEffect(() => {
    if (platform !== "android") return;

    const handleInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handleInstallPrompt);
    return () => {
      window.removeEventListener("beforeinstallprompt", handleInstallPrompt);
    };
  }, [platform]);

  const showAndroidInstallPrompt = async () => {
    if (!installPrompt) return;

    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  const storeLinks = [
    {
      platform: "apple" as const,
      href: APP_STORE_URL,
      label: "Download FarOut on the App Store",
      badge: <AppStoreBadge aria-hidden="true" focusable="false" />,
    },
    {
      platform: "google" as const,
      href: playStoreUrl,
      label: "Get FarOut on Google Play",
      badge: <PlayStoreBadge aria-hidden="true" focusable="false" />,
    },
  ];

  if (platform === "android") storeLinks.reverse();

  return (
    <S.Page>
      <S.Header>
        <S.HeaderLogoLink
          href="https://faroutguides.com"
          aria-label="Visit FarOut Guides"
        >
          <FarOutLogo aria-hidden="true" focusable="false" />
        </S.HeaderLogoLink>
      </S.Header>

      <S.Main>
        <S.Card aria-labelledby="go-landing-title">
          <S.AppIcon src="/logo192.png" alt="" />
          <S.Eyebrow>{GO_LANDING_COPY.eyebrow[context]}</S.Eyebrow>
          <S.Title id="go-landing-title">{GO_LANDING_COPY.title}</S.Title>
          <S.Description>{GO_LANDING_COPY.description[context]}</S.Description>
          {context !== "express" && (
            <S.OpenAppLink href={openAppUrl}>
              {GO_LANDING_COPY.openApp}
            </S.OpenAppLink>
          )}

          <S.Divider>{GO_LANDING_COPY.storesLead[context]}</S.Divider>
          <S.StoreLinks aria-label="Download FarOut">
            {storeLinks.map(store => (
              <S.StoreLink
                key={store.platform}
                href={store.href}
                aria-label={store.label}
              >
                {store.badge}
              </S.StoreLink>
            ))}
          </S.StoreLinks>
          <S.DownloadHelp>
            {GO_LANDING_COPY.downloadHelp[context]}
          </S.DownloadHelp>
        </S.Card>
      </S.Main>

      {installPrompt && (
        <S.AndroidPrompt aria-label="Install FarOut" aria-live="polite">
          <S.AndroidPromptIcon src="/logo192.png" alt="" />
          <S.AndroidPromptCopy>
            <S.AndroidPromptTitle>Get FarOut</S.AndroidPromptTitle>
            <S.AndroidPromptText>Open this link in the app</S.AndroidPromptText>
          </S.AndroidPromptCopy>
          <S.AndroidInstallButton onClick={showAndroidInstallPrompt}>
            Install
          </S.AndroidInstallButton>
          <S.AndroidDismissButton
            type="button"
            aria-label="Dismiss install prompt"
            onClick={() => setInstallPrompt(null)}
          >
            <CloseIcon />
          </S.AndroidDismissButton>
        </S.AndroidPrompt>
      )}
    </S.Page>
  );
};
